import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useIdleLock — 30-minute inactivity guard.
 *
 * Watches mousemove / keydown / scroll / click / touchstart. If no
 * activity for `idleMs`, the caller is told to freeze the UI. The
 * freeze timestamp is persisted in `sessionStorage` so a page reload
 * during a freeze stays locked (until the user completes an
 * `inactivity_unlock` Security Pulse).
 *
 * Returns:
 *   { frozen, freezeNow, unfreeze, remainingMs }
 */
const STORAGE_KEY = "vinci_idle_lock_frozen_at";
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll",
                "touchstart", "click", "wheel"];

export function useIdleLock({ idleMs = 30 * 60 * 1000, enabled = true } = {}) {
  const [frozen, setFrozen] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) != null; }
    catch { return false; }
  });
  const lastActivityRef = useRef(Date.now());
  const [tick, setTick] = useState(0);

  const freezeNow = useCallback(() => {
    try { sessionStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    setFrozen(true);
  }, []);

  const unfreeze = useCallback(() => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    lastActivityRef.current = Date.now();
    setFrozen(false);
  }, []);

  // Reset timer on any activity — but only while unfrozen.
  useEffect(() => {
    if (!enabled) return;
    if (frozen) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    return () => EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
  }, [enabled, frozen]);

  // Tick every 5 s to sample the idle window.
  useEffect(() => {
    if (!enabled) return;
    if (frozen) return;
    const t = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= idleMs) freezeNow();
      else setTick((n) => (n + 1) & 0xff);
    }, 5000);
    return () => clearInterval(t);
  }, [enabled, idleMs, frozen, freezeNow]);

  const remainingMs = Math.max(0, idleMs - (Date.now() - lastActivityRef.current));

  return { frozen, freezeNow, unfreeze, remainingMs, _tick: tick };
}
